import {useMemo, useEffect, useCallback} from 'react';
import {useFormContext, FieldError} from 'react-hook-form';
import {ControllerWrapper} from '@/components/common';
import {
  SoopDropdown,
  DropdownProps as SoopDropdownProps,
} from '@soop-ui/soop-ui';
import Dropdown from '@/components/help/common/FormContainer/Dropdown';
import FormError from '@/components/help/common/FormContainer/FormError';
import styles from '@/components/help/common/FormContainer/FormContainer.module.scss';
import useGlobalUIStore from '@/stores';
import {useHelpContext} from '@/app/(main)/routes/help/context/HelpContext';
import {useCommonSubCategory, useFormLabels} from '@/hooks/help/index';
import {FORM_FIELDS} from '@/constants/help/form';
import {useFormContainerContext} from '@/components/help/common/FormContainer/FormContainerContext';
import {
  FORM_TYPE_INQUIRY,
  FORM_TYPE_REPORT,
  FORM_TYPE_ILLEGAL,
} from '@/constants/help/form';
import {
  INQUIRY_MAIN_CATEGORY,
  REPORT_MAIN_CATEGORY,
  ILLEGAL_MAIN_CATEGORY,
} from '@/constants/help/index';
import {
  FormType,
  ListOption,
} from '@/components/help/common/FormContainer/types';

const MAIN_CATEGORY = (type: FormType) => {
  if (type === FORM_TYPE_INQUIRY) {
    return INQUIRY_MAIN_CATEGORY;
  }
  if (type === FORM_TYPE_REPORT) {
    return REPORT_MAIN_CATEGORY;
  }
  if (type === FORM_TYPE_ILLEGAL) {
    return ILLEGAL_MAIN_CATEGORY;
  }
  return [];
};
interface CategoryProps extends SoopDropdownProps {
  showDetailType?: boolean;
}

const Category = ({showDetailType = true}: CategoryProps) => {
  const {isDarkMode} = useGlobalUIStore();
  const {isMobile} = useHelpContext();
  const {mainCategoryType, title} = useFormLabels();
  const {type} = useFormContainerContext();

  const {
    watch,
    setValue,
    formState: {errors},
  } = useFormContext();

  const MAIN_CATEGORY_ERROR_MESSAGE = `${mainCategoryType} 선택해 주세요.`;
  const SUB_CATEGORY_ERROR_MESSAGE = '세부 유형 선택해 주세요.';

  // 메인 카테고리 옵션
  const mainCategoryOptions = useMemo<ListOption[]>(
    () => MAIN_CATEGORY(type),
    [type],
  );

  // 선택된 메인 카테고리 값(= ListOption.value)
  const mainCategoryValue = watch(FORM_FIELDS.MAIN_CATEGORY) as
    | string
    | undefined;

  // 서브 카테고리 조회
  const {data: detailTypesData} = useCommonSubCategory(mainCategoryValue);

  // 서브 카테고리 옵션
  const subCategoryOptions = useMemo<ListOption[]>(() => {
    const list =
      (
        detailTypesData as
          | {data?: {nodeList?: Array<{nodeName: string; nodeId: string}>}}
          | undefined
      )?.data?.nodeList ?? [];
    return list.map((node) => ({
      label: node.nodeName,
      value: node.nodeId,
    }));
  }, [detailTypesData]);

  // 메인 카테고리가 바뀌면 서브 카테고리 초기화(+에러 제거)
  useEffect(() => {
    setValue(FORM_FIELDS.SUB_CATEGORY, '');
  }, [mainCategoryValue, setValue]);

  const isIllegalType = type === FORM_TYPE_ILLEGAL;
  const iconFill = isDarkMode ? '#D5D7DC' : '#525661';

  const commonMainProps = {
    placeholder: '문의 유형 선택',
    disabled: isIllegalType,
    iconFill,
  } as const;

  const commonSubProps = {
    placeholder: '세부 유형 선택',
    iconFill,
  } as const;

  return (
    <section className={styles.section}>
      <label className={styles.label}>
        {`${title} 유형`} <em>*</em>
      </label>

      <div className={styles.categoryContainer}>
        {/* 메인 카테고리 */}
        <div
          id='mainCategory-wrapper'
          className={styles.flexInput}
          {...(isMobile && {tabIndex: 0})}>
          {isMobile ? (
            <ControllerWrapper
              name={FORM_FIELDS.MAIN_CATEGORY}
              component={SoopDropdown}
              mobile={isMobile}
              needsBlurWrapper
              className={errors.mainCategory ? styles.errorBorder : undefined}
              size={44}
              items={mainCategoryOptions}
              rules={{required: MAIN_CATEGORY_ERROR_MESSAGE}}
              error={!!errors.mainCategory}
              tabIndex={-1}
              {...commonMainProps}
            />
          ) : (
            <ControllerWrapper
              name={FORM_FIELDS.MAIN_CATEGORY}
              component={Dropdown}
              itemList={mainCategoryOptions}
              rules={{required: MAIN_CATEGORY_ERROR_MESSAGE}}
              error={!!errors.mainCategory}
              {...commonMainProps}
            />
          )}
        </div>

        {/* 서브 카테고리 */}
        {showDetailType && (
          <div
            id='subCategory-wrapper'
            className={styles.flexInput}
            {...(isMobile && {tabIndex: 0})}>
            {isMobile ? (
              <ControllerWrapper
                name={FORM_FIELDS.SUB_CATEGORY}
                component={SoopDropdown}
                mobile={isMobile}
                needsBlurWrapper
                className={errors.subCategory ? styles.errorBorder : undefined}
                size={44}
                items={subCategoryOptions}
                rules={{required: SUB_CATEGORY_ERROR_MESSAGE}}
                error={!!errors.subCategory}
                tabIndex={-1}
                {...commonSubProps}
              />
            ) : (
              <ControllerWrapper
                name={FORM_FIELDS.SUB_CATEGORY}
                component={Dropdown}
                itemList={subCategoryOptions}
                rules={{required: SUB_CATEGORY_ERROR_MESSAGE}}
                error={!!errors.subCategory}
                {...commonSubProps}
              />
            )}
          </div>
        )}

        {/* 에러 메시지 */}
        <FormError error={errors.mainCategory as FieldError} />
        <FormError error={errors.subCategory as FieldError} />
      </div>
    </section>
  );
};

export default Category;
